import { shouldOpenConfirmExitFloorplanOperationPrompt } from 'spw-components/floorplan/floor-plan-map-utils';
import { REQUEST_STATUS } from 'spw-constants';
import {
  ALL_ENABLED,
  FloorMapStates,
  IFloorplanState,
  ISpace,
  MeasurementState,
  ToolbarMode,
} from './types';
import {
  Actions,
  ADD_ITEM_TO_EDIT_HISTORY,
  ARCHIVE_FLOORPLAN,
  CANCEL_FLOOR_PLAN_IMAGE,
  CLEAN_COPYING_FLOOR_USF_SHAPE_STATE,
  CLEAN_EDIT_HISTORY,
  DELETE_ACTIVE_SHAPE,
  DELETE_CLUSTER,
  DELETE_CLUSTER_FAILED,
  DELETE_CLUSTER_SUCCEEDED,
  ENABLE_ALL_VIEW_LAYERS,
  ENABLE_SEATCOUNT_BUTTON,
  EXIT_FLOORPLAN_OPERATION,
  EXIT_FLOORPLAN_OPERATION_CANCEL,
  EXIT_FLOORPLAN_OPERATION_CONFIRM,
  FETCH_CALIBRATION_SUCCEEDED,
  FETCH_FLOORPLAN,
  FETCH_FLOORPLAN_FAILED,
  FETCH_FLOORPLAN_PDF,
  FETCH_FLOORPLAN_PDF_FAILED,
  FETCH_FLOORPLAN_PDF_SUCCESS,
  FETCH_FLOORPLAN_SUCCEEDED,
  FETCH_SPACES_WORKSHEET_SPREADSHEET,
  FETCH_SPACES_WORKSHEET_SPREADSHEET_FAILED,
  FETCH_SPACES_WORKSHEET_SPREADSHEET_SUCCESS,
  FETCH_SUGGESTED_CALIBRATION_SUCCEEDED,
  GET_FLOOR_SPACES_SUCCEEDED,
  GET_SPACES,
  GET_SPACES_SUCCEEDED,
  LAYERS_BUILT_SUCCEEDED,
  POPULATE_CALIBRATION,
  POPULATE_CLUSTERS,
  POPULATE_FLOORPLAN_SHAPES,
  REMOVE_SPACE_SHAPE,
  RESET_FLOORPLAN,
  RESTORE_CONTROLS_STATE,
  SET_ACTIVE_SHAPE,
  SET_ADD_NOTE_DIALOG_STATE,
  SET_ADD_SPACE_DIALOG_STATE,
  SET_ADJUSTED_FLOORPLAN_ITEMS,
  SET_BULK_OPERATION_SOURCE_FLOOR_DATA,
  SET_BULK_SELECTION_STATE,
  SET_CHANGES_FOR_BULK_OPERATION_ARE_PENDING,
  SET_CONTROL_STATE_VALUE,
  SET_CONTROLS_STATE,
  SET_CURRENT_FLOORPLAN_UPLOAD,
  SET_DID_UPDATE_FLOOR_PLAN,
  SET_FLOOR_IMAGE,
  SET_FLOOR_MAP_STATE,
  SET_FLOORPLAN_GEOJSON,
  SET_FLOORPLAN_IMAGE,
  SET_FLOORPLAN_PREVIEW_IMAGE_BOUNDS,
  SET_FLOORPLAN_PREVIEW_STATE,
  SET_IS_ARCHIVING_FLOORPLAN,
  SET_IS_DRAWING_CALIBRATION,
  SET_IS_LOADING_FLOORPLAN_IMAGE,
  SET_IS_MEASURED_SHAPE_DIRTY,
  SET_IS_UPLOADING_PDF,
  SET_MAP_MOUNTED,
  SET_MEASUREMENT_STATE,
  SET_MINI_MODAL_STATE,
  SET_ORIGINAL_FILE_DATA_URL,
  SET_ORIGINAL_IMAGE_DIMENSIONS,
  SET_ORIGINAL_SHAPE,
  SET_PREVIOUS_MAP_BOUNDS_AND_ZOOM,
  SET_SELECTED_SPACE,
  SET_SELECTED_SPACES,
  SET_SELECTION_SHAPE_POINTS,
  SET_SHOW_SPACES_USF_LAYER,
  SET_SPACE_TO_UPDATE,
  SET_SUGGESTED_CALIBRATION,
  SET_TOOLBAR_MODE,
  SET_TOOLTIP_STATE,
  SET_WORKING_LAYER,
  SHOW_CANCEL_UPLOAD,
  SHOW_SEATCOUNT_LAYER,
  SHOW_SPACETYPE_LAYER,
  START_COPYING_FLOOR_USF_SHAPE,
  TOGGLE_INDIVIDUAL_SPACES_SUCCESS,
  TOGGLE_ONLY_WITH_NOTE,
  TOGGLE_ONLY_WITH_PHOTO,
  TOGGLE_OTHER_SPACES,
  TOGGLE_WORKSPACE_INTERMEDIATE,
  UPDATE_SPACE,
  UPLOAD_FLOORPLAN,
  UPLOAD_FLOORPLAN_FAILED,
  UPLOAD_FLOORPLAN_SUCCEEDED,
} from './actions';
import { setCurrentFloor } from '../projects/actions';

const initialState: IFloorplanState = {
  floorplanGeoJsonIsLoading: false,
  floorplanGeoJson: null,
  currentFloorplan: null,
  currentFloorplanUpload: null,
  isUploadingFloorplan: false,
  isUploadingPDF: false,
  isArchivingFloorplan: false,
  isDownloadingFloorplanPDF: false,
  editHistory: { commands: [] },
  imageData: null,
  showCancelUploadDialog: false,
  oldControlState: null,
  floorMapState: FloorMapStates.Disable,
  currentControlState: ALL_ENABLED,
  measurementState: MeasurementState.NONE,
  spacesMap: new Map<string, ISpace[]>(),
  floorSpaces: {},
  needToBuildLayers: false,
  selectedSpace: null,
  spaceToUpdate: null,
  floorplanRequestStatus: REQUEST_STATUS.NONE,
  isLoadingFloorplanImage: false,
  originalCalibration: null,
  calibration: null,
  isDrawingCalibrationLine: false,
  isMeasuredShapeDirty: false,
  previousMapBoundsAndZoom: null,
  activeShape: null,
  floorUsfShapeCopy: null,
  shapes: [],
  clusters: [],
  didUpdate: false,
  bulkSelectionState: null,
  selectedSpaceLayers: [],
  bulkOperationSourceFloorCalibration: null,
  originalImageDimensions: null,
  originalFileDataUrl: '',
  isExitFloorplanOperationDialogOpen: false,
  mapMounted: false,
  isDownloadingWorksheetSpreadsheet: false,
  miniModalState: {
    isUpdateSpaceModalOpen: false,
    anchorEl: null,
    space: null,
    currentMarkerPosition: null,
    updateSpaceDialogInitialPosition: null,
  },
  addNoteDialogState: {
    isOpen: false,
    currentMarkerPosition: null,
  },
  addSpaceDialogState: {
    isOpen: false,
    currentMarkerPosition: null,
    anchorEl: null,
    newSpaceMarker: null,
  },
  isDeletingCluster: false,
  toolbarMode: ToolbarMode.NONE,
  previewState: null,
  previewImageBounds: null,
  isLoadingSpaces: false,
  adjustedFloorplanItems: {
    spaces: null,
    shapes: null,
    calibration: null,
  },
  changesForBulkOperationArePending: false,
};

const reducer = (
  state: IFloorplanState = initialState,
  action: Actions | ReturnType<typeof setCurrentFloor>
): IFloorplanState => {
  switch (action.type) {
    case SET_BULK_SELECTION_STATE:
      if (!state.bulkSelectionState && action.payload) {
        return {
          ...state,
          bulkSelectionState: action.payload,
          oldControlState: {
            ...state.oldControlState,
            showOtherSpaces: state.currentControlState.showOtherSpaces,
          },
          currentControlState: {
            ...state.currentControlState,
            showOtherSpaces: false,
          },
        };
      }

      if (!action.payload && state.bulkSelectionState) {
        return {
          ...state,
          bulkSelectionState: action.payload,
          oldControlState: {
            ...state.oldControlState,
            showOtherSpaces: null,
          },
          currentControlState: {
            ...state.currentControlState,
            showOtherSpaces: state.oldControlState?.showOtherSpaces,
          },
        };
      }

      return {
        ...state,
        bulkSelectionState: action.payload,
      };
    case SET_MINI_MODAL_STATE:
      return {
        ...state,
        miniModalState: { ...state.miniModalState, ...action.payload },
      };
    case SET_ADD_NOTE_DIALOG_STATE:
      return {
        ...state,
        addNoteDialogState: { ...state.addNoteDialogState, ...action.payload },
      };
    case SET_ADD_SPACE_DIALOG_STATE:
      return {
        ...state,
        addSpaceDialogState: {
          ...state.addSpaceDialogState,
          ...action.payload,
        },
      };
    case SET_SELECTED_SPACES:
      return {
        ...state,
        selectedSpaceLayers: action.payload,
      };
    case SET_BULK_OPERATION_SOURCE_FLOOR_DATA:
      return {
        ...state,
        floorNameWhereObjectsComeFrom: action.payload.floorName,
        bulkOperationSourceFloorCalibration: action.payload.calibration,
      };
    case SET_SELECTION_SHAPE_POINTS:
      return {
        ...state,
        selectionShapePoints: action.payload,
      };
    case LAYERS_BUILT_SUCCEEDED:
      return {
        ...state,
        needToBuildLayers: false,
      };
    case SET_FLOOR_MAP_STATE:
      return {
        ...state,
        floorMapState: action.payload.state,
      };
    case SET_IS_LOADING_FLOORPLAN_IMAGE:
      return {
        ...state,
        isLoadingFloorplanImage: action.payload.isLoadingFloorplanImage,
      };
    case SET_IS_UPLOADING_PDF:
      return {
        ...state,
        isUploadingPDF: action.payload.isUploadingPDF,
      };
    case RESET_FLOORPLAN:
      return {
        ...initialState,
        oldControlState: state.currentControlState,
        originalFileDataUrl: state.originalFileDataUrl,
        originalImageDimensions: state.originalImageDimensions,
        isUploadingFloorplan: state.isUploadingFloorplan,
        floorNameWhereObjectsComeFrom: state.floorNameWhereObjectsComeFrom,
        bulkOperationSourceFloorCalibration:
          state.bulkOperationSourceFloorCalibration,
        selectedSpaceLayers: state.selectedSpaceLayers,
        bulkSelectionState: state.bulkSelectionState,
        selectionShapePoints: state.selectionShapePoints,
        tooltipPositionState: state.tooltipPositionState,
        floorUsfShapeCopy: state.floorUsfShapeCopy,
      };
    case SHOW_CANCEL_UPLOAD:
      return {
        ...state,
        showCancelUploadDialog: action.payload.show,
      };
    case CANCEL_FLOOR_PLAN_IMAGE:
      return {
        ...state,
        previewState: null,
        imageData: null,
      };
    case SET_FLOORPLAN_IMAGE:
      return {
        ...state,
        currentFloorplan: {
          ...state.currentFloorplan,
          s3Path: action.payload.s3Path,
        },
      };
    case SET_CURRENT_FLOORPLAN_UPLOAD:
      return {
        ...state,
        currentFloorplanUpload: action.payload.floorplanUpload,
      };
    case CLEAN_EDIT_HISTORY:
      return {
        ...state,
        editHistory: {
          commands: [],
        },
      };
    case SET_ORIGINAL_IMAGE_DIMENSIONS: {
      const { dimensions } = action.payload;

      return {
        ...state,
        originalImageDimensions: dimensions,
      };
    }
    case SET_FLOOR_IMAGE:
      return {
        ...state,
        imageData: action.payload.image,
      };
    case ADD_ITEM_TO_EDIT_HISTORY: {
      const { editHistory } = state;

      return {
        ...state,
        editHistory: {
          ...editHistory,
          commands: [...editHistory.commands, action.payload.editingCommand],
        },
      };
    }
    case SET_DID_UPDATE_FLOOR_PLAN:
      return {
        ...state,
        didUpdate: action.payload.didUpdate,
      };
    case GET_SPACES:
      return {
        ...state,
        needToBuildLayers: false,
        isLoadingSpaces: true,
      };
    case GET_SPACES_SUCCEEDED:
      return {
        ...state,
        spacesMap: action.payload.spacesMap,
        needToBuildLayers: true,
        isLoadingSpaces: false,
      };
    case GET_FLOOR_SPACES_SUCCEEDED:
      return {
        ...state,
        floorSpaces: action.payload.floorSpaces,
      };

    case FETCH_FLOORPLAN:
      return {
        ...state,
        suggestedCalibration: null,
        floorplanRequestStatus: REQUEST_STATUS.QUERYING,
        isLoadingFloorplanImage: true,
      };
    case FETCH_FLOORPLAN_SUCCEEDED:
      return {
        ...state,
        currentFloorplan: action.payload.floorplan,
        floorMapState: FloorMapStates.Show,
        currentControlState: ALL_ENABLED,
        imageData: null,
        floorplanRequestStatus: REQUEST_STATUS.SUCCESS,
        isLoadingFloorplanImage: !!action.payload.floorplan?.s3Path,
      };
    case FETCH_FLOORPLAN_FAILED:
      return {
        ...state,
        floorplanRequestStatus: REQUEST_STATUS.FAILED,
        isLoadingFloorplanImage: false,
      };
    case SET_FLOORPLAN_GEOJSON:
      return {
        ...state,
        floorplanGeoJsonIsLoading: false,
        floorplanGeoJson: action.payload,
      };
    case UPLOAD_FLOORPLAN:
      return {
        ...state,
        isUploadingFloorplan: true,
      };
    case ARCHIVE_FLOORPLAN:
      return {
        ...state,
        isArchivingFloorplan: true,
      };
    case SET_IS_ARCHIVING_FLOORPLAN:
      return {
        ...state,
        isArchivingFloorplan: action.payload.isArchivingFloorplan,
      };
    case UPLOAD_FLOORPLAN_SUCCEEDED:
      return {
        ...state,
        currentFloorplan: action.payload.floorplan,
        floorMapState: FloorMapStates.Show,
        oldControlState: state.currentControlState,
        currentControlState: ALL_ENABLED,
        imageData: null,
        isUploadingFloorplan: false,
      };
    case UPLOAD_FLOORPLAN_FAILED:
      return {
        ...state,
        isUploadingFloorplan: false,
      };
    case TOGGLE_INDIVIDUAL_SPACES_SUCCESS:
      return {
        ...state,
        currentControlState: {
          ...state.currentControlState,
          showIndividualWorkSpaces:
            action.payload.show !== undefined
              ? action.payload.show
              : !state.currentControlState.showIndividualWorkSpaces,
        },
      };
    case SET_CONTROL_STATE_VALUE:
      return {
        ...state,
        currentControlState: {
          ...state.currentControlState,
          [action.payload.key]: action.payload.value,
        },
      };
    case TOGGLE_OTHER_SPACES:
      return {
        ...state,
        currentControlState: {
          ...state.currentControlState,
          showOtherSpaces:
            action.payload.show !== undefined
              ? action.payload.show
              : !state.currentControlState.showOtherSpaces,
        },
      };
    case TOGGLE_ONLY_WITH_PHOTO:
      return {
        ...state,
        currentControlState: {
          ...state.currentControlState,
          onlyShowPhotos:
            action.payload.show !== undefined
              ? action.payload.show
              : !state.currentControlState.onlyShowPhotos,
          onlyShowNotes: false,
          showSeatCountLayer: false,
          showSpaceTypeLayer: false,
        },
      };
    case TOGGLE_ONLY_WITH_NOTE:
      return {
        ...state,
        currentControlState: {
          ...state.currentControlState,
          onlyShowNotes:
            action.payload.show !== undefined
              ? action.payload.show
              : !state.currentControlState.onlyShowNotes,
          onlyShowPhotos: false,
          showSeatCountLayer: false,
          showSpaceTypeLayer: false,
        },
      };
    case SHOW_SEATCOUNT_LAYER:
      return {
        ...state,
        currentControlState: {
          ...state.currentControlState,
          showSeatCountLayer: action.payload.show,
        },
      };
    case SHOW_SPACETYPE_LAYER:
      return {
        ...state,
        currentControlState: {
          ...state.currentControlState,
          showSpaceTypeLayer: action.payload.show,
        },
      };
    case SET_SHOW_SPACES_USF_LAYER:
      return {
        ...state,
        currentControlState: {
          ...state.currentControlState,
          showSpacesUSFLayer: action.payload.show,
        },
      };
    case ENABLE_SEATCOUNT_BUTTON:
      return {
        ...state,
        currentControlState: {
          ...state.currentControlState,
          showSeatCountButton: action.payload.enable,
        },
      };
    case SET_CONTROLS_STATE:
      return {
        ...state,
        oldControlState: {
          ...state.currentControlState,
          showSeatCountLayer: false,
          showSpaceTypeLayer: false,
          showSpacesUSFLayer: false,
        },
        currentControlState: {
          ...state.currentControlState,
          ...action.payload.state,
        },
      };
    case RESTORE_CONTROLS_STATE:
      return {
        ...state,
        currentControlState: state.oldControlState
          ? state.oldControlState
          : state.currentControlState,
        oldControlState: null,
      };
    case SET_SELECTED_SPACE:
      return {
        ...state,
        selectedSpace: action.payload.space,
      };
    case SET_SPACE_TO_UPDATE:
      return {
        ...state,
        spaceToUpdate: action.payload.space,
      };
    case ENABLE_ALL_VIEW_LAYERS:
      return {
        ...state,
        currentControlState: ALL_ENABLED,
      };
    case TOGGLE_WORKSPACE_INTERMEDIATE:
      return {
        ...state,
        currentControlState: {
          ...state.currentControlState,
          showIndividualWorkSpacesIntermediate: action.payload.state,
        },
      };
    case SET_TOOLBAR_MODE:
      return {
        ...state,
        toolbarMode: action.payload,
      };
    case SET_MEASUREMENT_STATE:
      return {
        ...state,
        measurementState: action.payload,
        currentControlState:
          action.payload === MeasurementState.PRE_REDRAW
            ? {
                ...state.currentControlState,
                showIndividualWorkSpacesIntermediate: false,
                showIndividualWorkSpaces: true,
                showMeetingSpaces: true,
                showAmenitySpaces: true,
                showSupportSpaces: true,
                showOtherSpaces: true,
                showSpaceTypeLayer: false,
                onlyShowNotes: false,
                onlyShowPhotos: false,
                showSeatCountLayer: false,
                showSpacesUSFLayer: false,
              }
            : state.currentControlState,
      };
    case START_COPYING_FLOOR_USF_SHAPE:
      return {
        ...state,
        floorUsfShapeCopy: action.payload,
      };
    case CLEAN_COPYING_FLOOR_USF_SHAPE_STATE:
      return {
        ...state,
        floorUsfShapeCopy: null,
      };
    case FETCH_CALIBRATION_SUCCEEDED:
      return {
        ...state,
        originalCalibration: action.payload,
        calibration: action.payload,
      };
    case FETCH_SUGGESTED_CALIBRATION_SUCCEEDED:
    case SET_SUGGESTED_CALIBRATION:
      return {
        ...state,
        suggestedCalibration: action.payload,
      };
    case POPULATE_CALIBRATION:
      return {
        ...state,
        calibration: action.payload,
      };
    case SET_IS_DRAWING_CALIBRATION:
      return {
        ...state,
        isDrawingCalibrationLine: action.payload,
      };
    case SET_PREVIOUS_MAP_BOUNDS_AND_ZOOM:
      return {
        ...state,
        previousMapBoundsAndZoom: {
          ...state.previousMapBoundsAndZoom,
          ...action.payload,
        },
      };
    case SET_IS_MEASURED_SHAPE_DIRTY:
      return {
        ...state,
        isMeasuredShapeDirty: action.payload,
      };

    case SET_ACTIVE_SHAPE:
      return {
        ...state,
        activeShape: action.payload.shape,
      };
    case SET_ORIGINAL_SHAPE:
      return {
        ...state,
        originalShape: action.payload,
      };
    case POPULATE_FLOORPLAN_SHAPES:
      return {
        ...state,
        shapes: action.payload,
      };

    case DELETE_CLUSTER:
      return {
        ...state,
        isDeletingCluster: true,
      };
    case DELETE_CLUSTER_FAILED:
    case DELETE_CLUSTER_SUCCEEDED:
      return {
        ...state,
        isDeletingCluster: false,
      };
    case DELETE_ACTIVE_SHAPE:
      return {
        ...state,
        currentControlState: ALL_ENABLED,
      };
    case POPULATE_CLUSTERS:
      return {
        ...state,
        clusters: action.payload.clusters,
        needToBuildLayers: true,
      };
    case SET_WORKING_LAYER:
      return {
        ...state,
        workingLayer: action.payload,
      };

    case SET_ORIGINAL_FILE_DATA_URL:
      return {
        ...state,
        originalFileDataUrl: action.payload.originalFileDataUrl,
      };

    case EXIT_FLOORPLAN_OPERATION:
      return {
        ...state,
        isExitFloorplanOperationDialogOpen: shouldOpenConfirmExitFloorplanOperationPrompt(
          state.bulkSelectionState,
          state.floorUsfShapeCopy?.shape
        ),
      };

    case EXIT_FLOORPLAN_OPERATION_CONFIRM:
    case EXIT_FLOORPLAN_OPERATION_CANCEL:
      return {
        ...state,
        isExitFloorplanOperationDialogOpen: false,
      };

    case SET_TOOLTIP_STATE:
      return {
        ...state,
        tooltipPositionState: action.payload.tooltipPositionState,
      };

    case SET_FLOORPLAN_PREVIEW_IMAGE_BOUNDS:
      return {
        ...state,
        previewImageBounds: action.payload,
      };

    case SET_FLOORPLAN_PREVIEW_STATE:
      return {
        ...state,
        previewState: action.payload,
      };

    case SET_MAP_MOUNTED:
      return {
        ...state,
        mapMounted: action.payload.mapMounted,
      };
    case UPDATE_SPACE:
      return {
        ...state,
        selectedSpace: action.payload.shouldUpdateSelectedSpace
          ? action.payload.space
          : state.selectedSpace,
      };
    case REMOVE_SPACE_SHAPE:
      return {
        ...state,
        shapes: state.shapes.filter((shape) => shape.id !== action.payload.id),
      };

    case FETCH_SPACES_WORKSHEET_SPREADSHEET:
      return {
        ...state,
        isDownloadingWorksheetSpreadsheet: true,
      };
    case FETCH_SPACES_WORKSHEET_SPREADSHEET_SUCCESS:
    case FETCH_SPACES_WORKSHEET_SPREADSHEET_FAILED:
      return {
        ...state,
        isDownloadingWorksheetSpreadsheet: false,
      };
    case FETCH_FLOORPLAN_PDF:
      return {
        ...state,
        isDownloadingFloorplanPDF: true,
      };
    case FETCH_FLOORPLAN_PDF_SUCCESS:
    case FETCH_FLOORPLAN_PDF_FAILED:
      return {
        ...state,
        isDownloadingFloorplanPDF: false,
      };
    case SET_ADJUSTED_FLOORPLAN_ITEMS:
      return {
        ...state,
        adjustedFloorplanItems: action.payload,
      };

    case SET_CHANGES_FOR_BULK_OPERATION_ARE_PENDING:
      return {
        ...state,
        changesForBulkOperationArePending: action.payload,
      };

    default:
      return state;
  }
};

export default reducer;
